// MateScanTab.tsx
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import AstriaUKV2Shell from "../AstriaUKV2Shell";
import { useUKV2Colors } from "../AstriaUKV2ThemeContext";
import {
  AstriaUKV2ResultCard,
  AstriaUKV2FieldBlock,
  AstriaUKV2ActionList,
  UKV2LoadingState,
  UKV2ErrorState,
  UKV2ChatCta,
} from "../AstriaUKV2Card";
import {
  UKV2PersonFields,
  UKV2SubmitButton,
  PersonDetails,
  EMPTY_PERSON_DETAILS,
} from "../AstriaUKV2Wizard";
import { useAstriaUKV2Tab } from "../useAstriaUKV2Tab";
import { AstriaUKV2MateScanData, Message } from "../../../../contexts/ChatContext";

const SUBCATEGORY_NAME = "MateScan";

const buildMessage = (self: PersonDetails, partner: PersonDetails) => `
Give me a MateScan (fun, witty, cheeky compatibility snapshot) for me and ${partner.name.trim() || "them"}.
My Date of Birth: ${self.dob.trim()}.
My Time of Birth: ${self.time.trim() || "Not provided"}.
My Place of Birth: ${self.place.trim() || "Not provided"}.
${partner.name.trim() || "My partner"}'s Date of Birth: ${partner.dob.trim()}.
${partner.name.trim() || "My partner"}'s Time of Birth: ${partner.time.trim() || "Not provided"}.
${partner.name.trim() || "My partner"}'s Place of Birth: ${partner.place.trim() || "Not provided"}.
`.trim();

// Colors must be read INSIDE AstriaUKV2Shell's subtree (see the note in
// ZodiacPersonalityTab.tsx) — these small components are rendered as
// `children`, not evaluated inline, so their hook calls resolve after the
// shell's theme provider has mounted.

const ResultView: React.FC<{
  data?: AstriaUKV2MateScanData;
  message: Message | null;
  reset: () => void;
  goToChat: () => Promise<void>;
}> = ({ data, message, reset, goToChat }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  if (data) {
    return (
      <>
        <AstriaUKV2ResultCard
          icon={<Sparkles className="w-5 h-5" />}
          title={t("ukv2.tabs.mateScan.label")}
          subtitle={t("ukv2.mateScan.subtitle")}
          shareText={`${data.opening}\n\n${data.compatibility_snap}\n\n${data.closing}`}
        >
          <AstriaUKV2FieldBlock label={t("ukv2.common.opening")} value={data.opening} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.vibeCheck")} value={data.vibe_check} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.compatibilitySnap")} value={data.compatibility_snap} />
          <AstriaUKV2FieldBlock label={t("ukv2.field.chaosFactor")} value={data.chaos_factor} />
          <AstriaUKV2ActionList label={t("ukv2.common.today")} items={data.fun_action_plan?.today || []} />
          <AstriaUKV2ActionList label={t("ukv2.common.thisWeek")} items={data.fun_action_plan?.this_week || []} />
          <AstriaUKV2FieldBlock label={t("ukv2.common.closing")} value={data.closing} />
        </AstriaUKV2ResultCard>
        <button
          onClick={reset}
          className="text-xs font-semibold transition-colors"
          style={{ marginTop: 14, color: colors.mutedColor }}
        >
          {t("ukv2.common.startNewReading")}
        </button>
        <UKV2ChatCta onClick={goToChat} />
      </>
    );
  }

  if (message) {
    return (
      <AstriaUKV2ResultCard icon={<Sparkles className="w-5 h-5" />} title={t("ukv2.tabs.mateScan.label")}>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.75,
            color: colors.bodyColor,
            whiteSpace: "pre-wrap",
          }}
        >
          {message.content}
        </p>
      </AstriaUKV2ResultCard>
    );
  }

  return null;
};

const IntroForm: React.FC<{
  self: PersonDetails;
  setSelf: (v: PersonDetails) => void;
  partner: PersonDetails;
  setPartner: (v: PersonDetails) => void;
  onSubmit: () => void;
  formValid: boolean;
}> = ({ self, setSelf, partner, setPartner, onSubmit, formValid }) => {
  const { t } = useTranslation();
  const { colors } = useUKV2Colors();

  return (
    <>
      <div style={{ marginBottom: 20 }}>
        <h2
          style={{
            fontSize: 21,
            fontWeight: 700,
            color: colors.titleColor,
            marginBottom: 6,
          }}
        >
          {t("ukv2.tabs.mateScan.label")}
        </h2>
        <p style={{ fontSize: 13.5, color: colors.mutedColor, lineHeight: 1.6 }}>
          {t("ukv2.mateScan.intro")}
        </p>
      </div>

      <UKV2PersonFields
        heading={t("ukv2.common.yourDetails")}
        values={self}
        onChange={setSelf}
        namePlaceholder={t("ukv2.common.yourName")}
      />
      <UKV2PersonFields heading={t("ukv2.common.theirDetails")} values={partner} onChange={setPartner} />

      <UKV2SubmitButton onClick={onSubmit} disabled={!formValid} label={t("ukv2.mateScan.submit")} />
    </>
  );
};

const MateScanTab: React.FC = () => {
  const { t } = useTranslation();
  const { loading, error, message, send, reset, goToChat } = useAstriaUKV2Tab({
    subCategoryName: SUBCATEGORY_NAME,
  });

  const [self, setSelf] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);
  const [partner, setPartner] = useState<PersonDetails>(EMPTY_PERSON_DETAILS);

  const started = !!message || loading || !!error;
  const formValid = !!self.dob.trim() && !!partner.dob.trim();

  const handleSubmit = () => send(buildMessage(self, partner));

  const data = message?.astriaUKV2Data as AstriaUKV2MateScanData | undefined;

  if (started) {
    return (
      <AstriaUKV2Shell active="matescan">
        {loading && <UKV2LoadingState label={t("ukv2.loading.mateScan")} />}
        {!loading && error && <UKV2ErrorState message={error} onRetry={handleSubmit} />}
        {!loading && !error && (
          <ResultView data={data} message={message} reset={reset} goToChat={goToChat} />
        )}
      </AstriaUKV2Shell>
    );
  }

  return (
    <AstriaUKV2Shell active="matescan">
      <IntroForm
        self={self}
        setSelf={setSelf}
        partner={partner}
        setPartner={setPartner}
        onSubmit={handleSubmit}
        formValid={formValid}
      />
    </AstriaUKV2Shell>
  );
};

export default MateScanTab;
